import React from 'react'
import styled from 'styled-components'

const StyledColorPicker = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.small};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: ${({ theme }) => theme.spacing.xsmall};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

const StyledColorButton = styled.button<{ $color: string }>`
  width: 24px;
  height: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ $color }) => $color};
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
    border-color: ${({ theme }) => theme.colors.border.default};
  }
`

interface ColorPickerProps {
  onColorSelect: (color: string) => void
  onClose: () => void
}

const colors = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#cccccc',
  '#efefef',
  '#f3f3f3',
  '#ffffff',
  '#ff0000',
  '#ff9900',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#0000ff',
  '#9900ff',
  '#ff00ff',
  '#f4cccc',
  '#fce5cd',
  '#fff2cc',
  '#d9ead3',
  '#d0e0e3',
  '#cfe2f3',
  '#d9d2e9',
  '#ead1dc',
  '#ea9999',
  '#f9cb9c',
  '#ffe599',
  '#b6d7a8',
  '#a2c4c9',
  '#9fc5e8',
  '#b4a7d6',
  '#d5a6bd',
  '#e06666',
  '#f6b26b',
  '#ffd966',
  '#93c47d',
  '#76a5af',
  '#6fa8dc',
  '#8e7cc3',
  '#c27ba0',
  '#cc0000',
  '#e69138',
  '#f1c232',
  '#6aa84f',
  '#45818e',
  '#3d85c6',
  '#674ea7',
  '#a64d79',
  '#990000',
  '#b45f06',
  '#bf9000',
  '#38761d',
  '#134f5c',
  '#0b5394',
  '#351c75',
  '#741b47',
  '#660000',
  '#783f04',
  '#7f6000',
  '#274e13',
  '#0c343d',
  '#073763',
  '#20124d',
  '#4c1130',
]

export const ColorPicker: React.FC<ColorPickerProps> = ({
  onColorSelect,
  onClose,
}) => {
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      // Check if click is outside color picker and not on the palette button
      if (
        !target.closest('[data-color-picker]') &&
        !target.closest('[data-color-button]')
      ) {
        onClose()
      }
    }

    // Use setTimeout to avoid immediate closure on opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [onClose])

  return (
    <StyledColorPicker data-color-picker onClick={(e) => e.stopPropagation()}>
      {colors.map((color) => (
        <StyledColorButton
          key={color}
          $color={color}
          onClick={(e) => {
            e.stopPropagation()
            onColorSelect(color)
          }}
          title={color}
        />
      ))}
    </StyledColorPicker>
  )
}
